import React, { useEffect, useMemo, useRef } from 'react';
import { Table, Card, Typography, Button, Space, Tag, Switch, Tooltip, message, Input, theme } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { getHotelList, getHotelListProcessClear, updateHotelProfileEnabledRequest } from '@/AppRedux/actions/admin/hotelList';
import { RootState } from '@/AppRedux/reducers';
import { useResponsive } from '@/hooks/useResponsive';
import { useInfiniteReveal } from '@/hooks/useInfiniteReveal';
import HotelMobileList from './components/HotelMobileList';


const { Title } = Typography;

type HotelData = {
    id: string;
    code: string;
    name: string;
    active?: boolean;
}

const HotelList: React.FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { hotelList, loading, error, process } = useSelector((state: RootState) => state.hotelList);
    const useCurrentFirstRender = useRef(true)
    const PAGE_SIZE = 20;
    const [searchParams, setSearchParams] = useSearchParams();
    const searchText = searchParams.get('q') ?? '';
    const currentPage = Number(searchParams.get('page')) || 1;
    const revealedCount = Number(searchParams.get('revealed')) || PAGE_SIZE;
    const { isMobile } = useResponsive();
    const { token } = theme.useToken();

    const filteredHotelList = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        if (!q) return hotelList;
        return hotelList.filter(h =>
            h.name.toLowerCase().includes(q) || h.code.toLowerCase().includes(q)
        );
    }, [hotelList, searchText]);

    const handleSearch = (value: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (value) next.set('q', value); else next.delete('q');
            next.set('page', '1');
            next.delete('revealed');
            return next;
        }, { replace: true });
    };

    const handlePageChange = (page: number) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('page', String(page));
            return next;
        }, { replace: true });
    };

    const handleRevealedCountChange = (count: number) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('revealed', String(count));
            return next;
        }, { replace: true });
    };

    useEffect(() => {
        if (hotelList.length === 0) return; // initial fetch hasn't completed yet — don't clobber a restored page from the URL
        const totalPages = Math.max(1, Math.ceil(filteredHotelList.length / PAGE_SIZE));
        if (currentPage > totalPages) {
            handlePageChange(totalPages);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hotelList.length, filteredHotelList.length, currentPage]);

    const { visibleItems, hasMore, sentinelRef, loadMore } = useInfiniteReveal(filteredHotelList, PAGE_SIZE, searchText, {
        initialCount: revealedCount,
        onCountChange: handleRevealedCountChange,
    });

    useEffect(() => {
        if (useCurrentFirstRender.current) {
            dispatch(getHotelList())
            useCurrentFirstRender.current = false
        }
    }, [dispatch])

    useEffect(() => {
        if (process.status && process.action === 'UPDATE_HOTEL_PROFILE_REQUEST') {
            message.success(process.message);
            dispatch(getHotelListProcessClear());
        }
    }, [process, dispatch]);

    useEffect(() => {
        if (error) {
            message.error(error);
            dispatch(getHotelListProcessClear());
        }
    }, [error, dispatch]);

    const handleToggleEnabled = (record: HotelData, checked: boolean) => {
        dispatch(updateHotelProfileEnabledRequest({ id: record.id, active: checked }));
    };

    const handleEdit = (record: HotelData) => {
        navigate(`./${record.id}/edit`);
    };

    const handleCreate = () => {
        navigate(`./create`);
    };

    const columns: ColumnsType<HotelData> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 320,
            render: (text) => <Typography.Text copyable type="secondary" style={{ fontSize: '12px' }}>{text}</Typography.Text>,
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 150,
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (_, record) => {
                const code = record.code.toLowerCase().replace(/\s+/g, '');
                const relativeUrl = `/administrator/${code}`;
                return (
                    <Link to={relativeUrl} target="_blank" rel="noopener noreferrer">
                        <Typography.Text strong underline>{record.name}</Typography.Text>
                    </Link>
                );
            },
        },
        {
            title: 'active',
            dataIndex: 'active',
            key: 'active',
            width: 50,
            align: 'center' as const,
            render: (_, record) => (
                <Tooltip title={record.active ? 'Disable' : 'Enable'}>
                    <Switch
                        checked={record.active}
                        onChange={(checked) => handleToggleEnabled(record, checked)}
                        size="small"
                        style={{ width: 40 }}
                    />
                </Tooltip>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            align: 'center' as const,
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title={intl.formatMessage({ id: 'common.edit', defaultMessage: 'Edit' })}>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        }
    ];

    return (
        <Card variant="outlined" style={{ borderRadius: 8, boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}>
            <div
                style={{
                    marginBottom: isMobile ? 12 : 24,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: isMobile ? 12 : 0,
                }}
            >
                <Title level={4} style={{ margin: 0 }}>Hotel List</Title>
                {isMobile ? (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                        style={{ width: '100%' }}
                    >
                        Create Hotel Profile
                    </Button>
                ) : (
                    <Space size="middle" orientation="horizontal">
                        <Input.Search
                            allowClear
                            defaultValue={searchText}
                            placeholder="Search by hotel name or code"
                            onChange={(e) => handleSearch(e.target.value)}
                            onSearch={handleSearch}
                            style={{ width: 280 }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleCreate}
                        >
                            Create Hotel Profile
                        </Button>
                    </Space>
                )}
            </div>
            {isMobile && (
                <div
                    style={{
                        position: 'sticky',
                        top: 64,
                        zIndex: 90,
                        background: token.colorBgContainer,
                        padding: '8px 0',
                        marginBottom: 8,
                        boxShadow: `0 4px 12px -6px ${token.colorBorderSecondary}`,
                    }}
                >
                    <Input.Search
                        allowClear
                        defaultValue={searchText}
                        placeholder="Search by hotel name or code"
                        onChange={(e) => handleSearch(e.target.value)}
                        onSearch={handleSearch}
                        style={{ width: '100%' }}
                    />
                </div>
            )}
            {isMobile ? (
                <HotelMobileList
                    hotels={visibleItems}
                    total={filteredHotelList.length}
                    loading={loading}
                    onEdit={handleEdit}
                    onToggleEnabled={handleToggleEnabled}
                    sentinelRef={sentinelRef}
                    hasMore={hasMore}
                    onLoadMore={loadMore}
                />
            ) : (
                <Table
                    columns={columns}
                    dataSource={filteredHotelList}
                    rowKey="id"
                    pagination={{
                        current: currentPage,
                        pageSize: PAGE_SIZE,
                        showSizeChanger: false,
                        onChange: (page) => handlePageChange(page),
                        showTotal: (total) => `Total ${total} hotels`,
                    }}
                    loading={loading}
                    scroll={{ x: 690 }}
                />
            )}
        </Card>
    );
};

export default HotelList;
